import {
  AlignmentType,
  Document,
  ImageRun,
  ISectionOptions,
  Packer,
  Paragraph,
  SectionType,
  TextRun,
} from "docx";

import { saveAs } from "file-saver";
//@ts-ignore
import { ref as firebaseRef, getBlob } from "firebase/storage";
import { Checklist, ChecklistCriteria } from "types/Checklist";
import { Paragraphs } from "types/Paragraph";
import { Revision } from "types/Revision";
import { SubmittedDocument } from "types/SubmittedDocument";
import { WordChecklistCells } from "types/WordChecklist";
import { useFirebaseStorage } from "vuefire";
import { RevisionCommentPlacement } from "~/types/enums/RevisionCommentPlacement.enum";
import { RevisionAnnotations } from "~/types/RevisionAnnotation";
import { useExtractNumberingConfig } from "./useExtractNumberingConfig";
import { usePopulateAnnotationMaps } from "./usePopulateAnnotationMaps";
import { useWordChecklistGenerator } from "./useWordChecklistGenerator";
import { useWordFontSize } from "./useWordFontSize";
import { useWordPageSize } from "./useWordPageSize";
import { useWordParagraphGenerator } from "./useWordParagraphGenerator";
export const useWordDocumentGenerator = () => {
  const exportWordDocument = async (
    submittedDocument: SubmittedDocument,
    revision: Revision,
    checklist: Checklist | undefined,
    allCriteria: ChecklistCriteria[] | undefined,
    wordChecklistCells: WordChecklistCells | undefined,
    paragraphs: Paragraphs,
    annotations: RevisionAnnotations,
    totalEarnedScore: number | undefined,
    earnedScore: number | undefined,
    commentColor: number[],
    returnBlob: boolean = false,
    hideScores: boolean = false,
    fileNameOverride: string | undefined = undefined
  ) => {
    if (!revision) {
      throw createError("Revision not found");
    }

    const revisionCommentPlacement =
      revision.commentPlacement ?? RevisionCommentPlacement.AfterDocument;

    paragraphs = useTrimParagraphs(paragraphs);

    const { annotationMap, annotationParagraphMap, endLineAnnotations } =
      usePopulateAnnotationMaps(annotations);

    var pageSize = useWordPageSize();

    var fontSize: number = useWordFontSize();

    const sections = [];

    if (
      checklist &&
      wordChecklistCells &&
      allCriteria &&
      totalEarnedScore != undefined &&
      earnedScore != undefined
    ) {
      const checklistSections = useWordChecklistGenerator(
        submittedDocument,
        checklist,
        wordChecklistCells,
        allCriteria,
        totalEarnedScore,
        earnedScore,
        hideScores
      );

      sections.push(...checklistSections);
    }

    var assignmentParagraphs: Paragraph[] = [];

    if (
      revision.commentObject &&
      revisionCommentPlacement == RevisionCommentPlacement.BeforeDocument
    ) {
      // @ts-ignore
      // var lines = nuxtApp.$convertQuillDeltaToLines(revision.commentObject);
      var lines = useConvertQuillDeltaToTextRuns(
        revision.commentObject,
        fontSize,
        useRGBToHex(commentColor)
      );

      const commentParagraphs: Paragraph[] = [];

      for (var line of lines) {
        assignmentParagraphs.push(
          new Paragraph({
            spacing: {
              line: 300,
            },
            children: line,
          })
        );
      }

      const commmentSection: ISectionOptions = {
        properties: {
          page: pageSize,
          type: SectionType.CONTINUOUS,
        },
        children: commentParagraphs,
      };

      sections.push(commmentSection);
    }

    const numberingConfig = useExtractNumberingConfig(paragraphs);

    // These are annotations that are long that I want to defer to the end of hte document.
    var previousParagraphCollectionRef = undefined;

    for (var paragraph of paragraphs) {
      var paragraphParagraphs = useWordParagraphGenerator(
        paragraph,
        numberingConfig,
        annotationMap,
        annotationParagraphMap,
        endLineAnnotations,
        previousParagraphCollectionRef,
        commentColor,
        checklist?.levelName?.includes("OSS") ?? false
      );

      previousParagraphCollectionRef = paragraph.collectionRef;

      assignmentParagraphs.push(...paragraphParagraphs);
    }

    if (revision.fileObject) {
      // Is the fileObject.mediaType an image?

      const isImage = revision.fileObject.mediaType.includes("image");

      if (isImage) {
        const imagePath = revision.fileObject.fullPath;

        const storage = useFirebaseStorage();
        const fileRef = firebaseRef(storage, imagePath);

        const imageBlob: any = await getBlob(fileRef);

        const { width: originalWidth, height: originalHeight } =
          await useImageDimensions(imageBlob);

        const imageBuffer = await imageBlob.arrayBuffer();
        // // const imageBase64String = Buffer.from(buffer).toString("base64");

        const pageWidthInTwips = 500;

        const imageRun = new ImageRun({
          data: imageBuffer,
          transformation: {
            width: pageWidthInTwips,
            height: Math.floor(
              (originalHeight / originalWidth) * pageWidthInTwips
            ),
          },
        });

        assignmentParagraphs.push(
          ...[
            new Paragraph({
              children: [imageRun, new TextRun(" ")],
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({}),
          ]
        );
      }
    }

    if (
      revision.commentObject &&
      revisionCommentPlacement == RevisionCommentPlacement.AfterDocument
    ) {
      // @ts-ignore
      // var lines = nuxtApp.$convertQuillDeltaToLines(revision.commentObject);
      var lines = useConvertQuillDeltaToTextRuns(
        revision.commentObject,
        fontSize,
        useRGBToHex(commentColor)
      );

      for (var line of lines) {
        assignmentParagraphs.push(
          new Paragraph({
            spacing: {
              line: 300,
            },
            children: line,
          })
        );
      }
    }

    const assignmentSection: ISectionOptions = {
      properties: {
        page: pageSize,
        type: SectionType.NEXT_PAGE,
      },
      children: assignmentParagraphs,
    };

    sections.push(assignmentSection);

    const doc = new Document({
      sections: sections,
      numbering: {
        config: numberingConfig,
      },
      compatibility: {},
      styles: {
        default: {
          document: {
            run: {
              size: 24,

              // size: 24, // Size is measured in half points, so 24 is equivalent to 12pt
            },
          },
        },
      },
    });

    const blob = await Packer.toBlob(doc);

    if (returnBlob == true) {
      return blob;
    }

    console.log("fileNameOverride", fileNameOverride);

    var docName = fileNameOverride ?? submittedDocument!.name;
    docName = docName.replace(".docx", "");
    if (fileNameOverride == undefined) {
      docName = `${docName}_graded.docx`;
    } else {
      docName = `${docName}.docx`;
    }

    saveAs(blob, docName);
  };

  return { exportWordDocument };
};
